import { IntlContext, useTranslator } from '@jutro/locale';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { readViewModelValue } from '@xengage/gw-jutro-adapters-react';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { SearchService } from 'gw-capability-gateway';
import PropTypes from 'prop-types';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { withRouter } from 'react-router-dom';
import messages from './SearchResults.messages';
import metadata from './SearchResults.metadata.json5';
import styles from './SearchResults.module.scss';
import { FieldLinkComponent } from 'gw-components-platform-react';
import _ from 'lodash'

// 2024-10-04 - Debra Frum - US26423 - PE - Search Accounts, Policies, Contacts - Solr Search
// Modified to perform FreeTextSearch which integrates with Solr
function SearchResults(props) {
    const {
        match: {
            params: { searchText },
        },
        param,
        title,
        noResultsMessage,
    } = props;
    const intl = useContext(IntlContext);
    const translator = useTranslator();
    const [searchObj, setSearchObj] = useState({
        previousSearchParam: '',
        searchParam: searchText,
    
        // 2024-10-04 - Debra Frum - US26423 - PE - Search Accounts, Policies, Contacts - Solr Search
        active: false,
        hasMoreResults: false,
        sortField: ''
    });
    const [queryOptions, setQueryOptions] = useState({
        pageNumber_TWIA: 0,
        orderBy: 'CreateTime',
        // orderByAscending: false
        orderByDescending: true
    });
    const [searchResult, setSearchResult] = useState({
        accounts: [],
        policies: [],
    });
    const [prevParam, setPrevParam] = useState('');
    const [loading, setLoading] = useState(true);

    const { authHeader } = useAuthentication();


     // 2024-11-14 - Debra Frum - US26423 - PE - Search Accounts, Policies, Contacts - Solr Search
     const getSearchObj = useCallback((searchParam, previousSearchParam, hasMoreResults) => {
        return {
            previousSearchParam: previousSearchParam,
            searchParam: searchParam,

            // 2024-10-04 - Debra Frum - US26423 - PE - Search Accounts, Policies, Contacts - Solr Search
            active: false,
            hasMoreResults: hasMoreResults,
            sortField: ''
        };
    }, []);

    const populateAccountResults = useCallback(data => {
        const accounts = [];
        if (data.accounts.length) {
            data.accounts.forEach(account => {
                if (account !== null) {
                    const accountInfo = {};
                    accountInfo.accountNumber = account.accountNumber;
                    accountInfo.accountName = account.accountHolder;
                    const { addressLine1, city, state, postalCode } =
                        account.accountHolderAddress;
                    const addressLine2Data = account.accountHolderAddress
                        .addressLine2
                        ? account.accountHolderAddress.addressLine2
                        : '';

                    accountInfo.addressLine = `${addressLine1}, ${addressLine2Data}`;
                    accountInfo.addressOthers = `${city}, ${state}${postalCode}`;
                    accounts.push(accountInfo);
                }
            });
        }
        return accounts;
    }, []);

    const populatePolicyResults = useCallback(data => {
        const policies = [];
        if (data.policies.length) {
            data.policies.forEach(policy => {
                // 2024-11-12 - Debra Frum - US26423 - PE - Search Accounts, Policies, Contacts - Solr Search
                // Display search results for jobs that don't have a policy number (Submissions not issued yet)
                if (policy.policyNumber || policy.jobNumber_TWIA) {
                    const policyInfo = {};

                    policyInfo.policyNumber = policy.policyNumber;
                    policyInfo.accountNumber = policy.accountNumber;
                   
                    // 2024-10-09 - Debra Frum - US26423 - PE - Search Accounts, Policies, Contacts - Solr Search
                    policyInfo.jobNumber_TWIA = policy.jobNumber_TWIA;
                    policyInfo.primaryNamedInsured = policy.primaryInsuredName;
                    // if (policy.accountHolder.subtype === 'Company') {
                    //     policyInfo.accountName =
                    //         policy.accountHolder.contactName;
                    // } else if (policy.accountHolder.subtype === 'Person') {
                    //     policyInfo.accountName = `${policy.accountHolder.firstName} ${policy.accountHolder.lastName}`;
                    // }

                    const { addressLine1, city, state, postalCode } =
                        policy.accountHolder.primaryAddress;
                    const addressLine2Data = policy.accountHolder.primaryAddress
                        .addressLine2
                        ? policy.accountHolder.primaryAddress.addressLine2
                        : '';
                    policyInfo.addressLine = `${addressLine1}, ${addressLine2Data}`;
                    policyInfo.addressOthers = `${city}, ${state}${postalCode}`;

                    // 2024-10-04 - Debra Frum - US26423 - PE - Search Accounts, Policies, Contacts - Solr Search
                    const insured = policy.primaryInsured_TWIA;
                    policyInfo.propertyAddress = policy.locationAddress_TWIA.displayName;
                    if (policy.locationAddress_TWIA.displayName !== insured.primaryAddress.displayName) {
                        policyInfo.mailingAddress = insured.primaryAddress.displayName;
                    }
                    policyInfo.effectiveDate = policy.effective
                    policyInfo.expiryDate = policy.expiration;
                    policyInfo.status = policy.displayStatus;
         
                    policies.push(policyInfo);
                }
            });
        }
        return policies;
    }, []);


    // 2024-10-09 - Debra Frum - US26423 - PE - Search Accounts, Policies, Contacts - Solr Search
    const getPolicyNumberLink = useCallback((item, index, { id: property }) => {
        const toolTipMessage = translator(messages.policyNumber);
        let redirectRoute = '';
        let redirectPath = '';
        let tableCellValue = '';

        // If there is a policy associated with item, display the Policy Number as a link 
        // to the Policy summary page
        if (!_.isNil(item.policyNumber)) {
            redirectRoute = 'policies';
            redirectPath = item.policyNumber;
            tableCellValue = item.policyNumber;
        // If there is a job associated with the item, display 'Unassigned' as a link 
        // to the Quote summary page
        } else if (!_.isNil(item.jobNumber_TWIA)) {
            redirectRoute = 'quotes';
            redirectPath = item.jobNumber_TWIA;
            tableCellValue = translator(messages.noPolicyNumber);
        // If there isn't a policy or job associated with the item, display 'Unassigned' with no link 
        } else {
            tableCellValue = translator(messages.noPolicyNumber);
        }

        if (redirectRoute === '') {
            return (
                <span title={translator(toolTipMessage)}>
                    {tableCellValue}
                </span>
            );
        } else {
            return (
                <FieldLinkComponent
                    id={`policy${item[property]}`}
                    to={`/${redirectRoute}/${redirectPath}/summary`}
                    className={styles.removeLinkStyle}
                    title={translator(toolTipMessage)}
                    value={tableCellValue}
                />
             );
        }
    }, [translator]);

    // 2024-10-09 - Debra Frum - US26423 - PE - Search Accounts, Policies, Contacts - Solr Search
    const getAccountNumberLink = useCallback((item, index, { id: property }) => {
        const { accountNumber } = item;

        return (
            <FieldLinkComponent
                id={`account${item[property]}`}
                to={`/accounts/${item[property]}/summary`}
                className={styles.removeLinkStyle}
                title={translator(messages.account)}
                value={item[property]}
            />
        );
    }, [translator]);

    // 2024-10-09 - Debra Frum - US26423 - PE - Search Accounts, Policies, Contacts - Solr Search
    const getPropertyAddress = useCallback((item, index, { id: property }) => {
        const toolTipMessage = translator(messages.propertyAddress);

        // Display Property Address.  Also display Mailing Address if it is different from the  Property Address.
        if (!_.isNil(item.mailingAddress)) {
            return (
                <span title={translator(toolTipMessage)}>
                    {item[property]}
                    <br/>
                    {translator(messages.mailingAddress)}{item.mailingAddress}
                 </span>
             );
        } else {
            return (
                <span title={translator(toolTipMessage)}>
                    {item[property]}
                </span>
            );
         }
    }, [translator]);

    // 2024-11-14 - Debra Frum - US26423 - PE - Search Accounts, Policies, Contacts - Solr Search
    const getFormattedDate = useCallback((item, index, path) => {
        return intl.formatDate(
            new Date(item[path.path]),
            {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
            }
        );
    }, []);


    const search = useCallback(() => {
        const searchParam = param || searchText;
        const fetchData = async () => {
            setLoading(true);
            // setPrevParam(searchParam);

            // 2024-10-04 - Debra Frum - US26423 - PE - Search Accounts, Policies, Contacts - Solr Search
            // const data = await SearchService.search(searchObj, authHeader);
            const data = await SearchService.search(
                searchObj, 
                queryOptions,
                authHeader
            );
            setLoading(false);

            setSearchResult(() => {
                return {
                    accounts: populateAccountResults(data),
                    policies: populatePolicyResults(data),
                };
            });

            setSearchObj(() => {
                return getSearchObj(searchParam,  searchParam, data.hasMoreResults);
            });
        };
       // if (prevParam !== searchParam) {
            fetchData();
       // }
    }, [
        searchText,
        populateAccountResults,
        populatePolicyResults,
        authHeader,
        param,
        prevParam,
    ]);

    
    // 2024-11-14- Debra Frum - US26423 - PE - Search Accounts, Policies, Contacts - Solr Search
    const onPrevious = () => {
        queryOptions.pageNumber_TWIA = queryOptions.pageNumber_TWIA - 1;
        search();
   }

    // 2024-11-14 - Debra Frum - US26423 - PE - Search Accounts, Policies, Contacts - Solr Search
    const onNext = () => {
        queryOptions.pageNumber_TWIA = queryOptions.pageNumber_TWIA + 1;
        search();
    }

    useEffect(() => {
        // Initialize a new search
        searchObj.searchParam = searchText;
        searchObj.previousSearchParam = '';
        searchObj.hasMoreResults = undefined;
        queryOptions.pageNumber_TWIA = 0;
        searchResult.accounts = [];
        searchResult.policies = [];
        
        search();
    }, [search]);


    const accountDetailsOverrides = useMemo(() => {
        const overrides = searchResult.accounts.map((account, index) => {
            return {
                [`accountAccountNameLink${index}`]: {
                    to: `/accounts/${account.accountNumber}/summary`,
                    content: `${account.accountName}`,
                },
                [`accountNumberLink${index}`]: {
                    to: `/accounts/${account.accountNumber}/summary`,
                    content: `# ${account.accountNumber}`,
                },
                [`accountAddressLine${index}`]: {
                    content: `${account.addressLine}`,
                },
                [`accountAddressOthers${index}`]: {
                    content: `${account.addressOthers}`,
                },
            };
        });

        return Object.assign({}, ...overrides);
    }, [searchResult.accounts]);

    // 2024-10-07 - Debra Frum - US26423 - PE - Search Accounts, Policies, Contacts - Solr Search
    //   Replace horizontal list of policies with a tablular list 
    //  const policyDetailsOverrides = useMemo(() => {
    //      const overrides = searchResult.policies.map((policy, index) => {
    //          return {
    //             [`policyNumberLink${index}`]: {
    //                 to: `/policies/${policy.policyNumber}/summary`,
    //             },
    //             [`policyNumber${index}`]: {
    //                 content: `# ${policy.policyNumber}`,
    //             },
    //             [`accountName${index}`]: {
    //                 content: `${policy.accountName}`,
    //             },
    //             [`policyAccountNameLink${index}`]: {
    //                 to: `/accounts/${policy.accountNumber}/summary`,
    //             },
    //             [`policyAddressLine${index}`]: {
    //                 content: `${policy.addressLine}`,
    //             },
    //             [`policyAddressOthers${index}`]: {
    //                 content: `${policy.addressOthers}`,
    //             },
    //             [`policyEffectiveDate${index}`]: {
    //                 content: `${policy.effective}`,
    //             },
    //             [`policyExpirationDate${index}`]: {
    //                 content: `${policy.expiration}`,
    //             },
    //        };
    //     });
    // 
    //     return Object.assign({}, ...overrides);
    // }, [searchResult.policies]);

    const overrideProps = {
        accountResults: {
            visible: searchResult.accounts.length > 0 && !loading,
        },
        searchResultsTitle: {
            content: title || translator(messages.searchResults),
        },
        policyResults: {
            visible: searchResult.policies.length > 0 && !loading,
        },
        searchingText: {
            visible: loading,
        },
        loadingIndicator: {
            loaded: !loading,
        },
        noResultsMessage: {
            visible:
                (!!searchText || param) &&
                !loading &&
                !searchResult.accounts.length &&
                !searchResult.policies.length,
            content:
                noResultsMessage ||
                translator(messages.noResultsFoundFor, {
                    query: searchText || param,
                }),
        },
        accountHeader: {
            content: translator(messages.accountResults, {
                accountResultsNumber: searchResult.accounts.length,
            }),
        },
        // 2024-11-12 - Debra Frum - US26423 - PE - Search Accounts, Policies, Contacts - Solr Search
        // Remove title from Search Result table
        // policyHeader: {
        //     content: translator(messages.policyResults, {
        //         policyResultsNumber: searchResult.policies.length,
        //     }),
        // },

        // 2024-10-07 - Debra Frum - US26423 - PE - Search Accounts, Policies, Contacts - Solr Search
        //   Replace horizontal list of policies with a tablular list 
        policiesTableGrid: {
            data: searchResult.policies,
            visible: searchResult.policies.length > 0,
        },
        previousButton: {
            visible: queryOptions.pageNumber_TWIA > 0 && !loading ,
        },
        nextButton: {
            visible: searchObj.hasMoreResults && !loading,
        },

        ...accountDetailsOverrides,
        // 2024-10-07 - Debra Frum - US26423 - PE - Search Accounts, Policies, Contacts - Solr Search
        //   Replace horizontal list of policies with a tablular list 
        //...policyDetailsOverrides,
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            getPolicyNumberLink: getPolicyNumberLink,
            getAccountNumberLink: getAccountNumberLink,
            getPropertyAddress: getPropertyAddress,
            getFormattedDate: getFormattedDate,
            getSearchObj: getSearchObj,
            search: search,
            onPrevious: onPrevious,
            onNext: onNext,
        },
    };

    const readValue = useCallback(
        (id, path) => {
            return readViewModelValue(
                metadata.pageContent,
                searchResult,
                id,
                path,
                overrideProps
            );
        },
        [overrideProps, searchResult]
    );

    return (
        <ViewModelForm
            uiProps={metadata.pageContent}
            model={searchResult}
            overrideProps={overrideProps}
            classNameMap={resolvers.resolveClassNameMap}
            callbackMap={resolvers.resolveCallbackMap}
            resolveValue={readValue}
        />
    );
}

SearchResults.propTypes = {
    match: PropTypes.shape({
        params: PropTypes.shape({
            searchText: PropTypes.string,
        }),
    }).isRequired,
    param: PropTypes.string,
    title: PropTypes.string,
    noResultsMessage: PropTypes.string,
};

SearchResults.defaultProps = {
    param: '',
    title: '',
    noResultsMessage: '',
};

export default withRouter(SearchResults);
